import { Navigate } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useAbility } from '../features/auth/abilityContext';
import { useRolesSettled } from '../features/auth/useRolesSettled';
import FullPageProgress from './FullPageProgress';

/**
 * The door to the office app: field-only crew (Mitarbeiter and Teamleitung
 * (Junior), who hold no office right at all) is sent to Feldaufnahme, whichever
 * way they arrived at an /office URL.
 *
 * A guard rather than the redirect at `/`, because that one only picks a
 * DEFAULT and every other way in walks past it — a bookmark, a reload, a
 * pre-split link, and above all a change of user: the login screen renders in
 * place of the routes and leaves the address bar alone.
 *
 * The wait matters as much as the check: until the roles replicate everyone
 * looks field-only, and bouncing then would throw office staff out of the page
 * they just reloaded (see useRolesSettled).
 *
 * This is the office/field SPLIT, not a permission boundary — the office pages
 * that carry real authority guard themselves (RequireAbility), and the server
 * enforces every write regardless.
 */
export default function RequireOffice({
    children
}: {
    children: ReactElement;
}) {
    const ability = useAbility();
    const settled = useRolesSettled();

    if (!settled) {
        return <FullPageProgress />;
    }
    if (!ability.can('access', 'Office')) {
        return <Navigate to="/field" replace />;
    }
    return children;
}
